import React, { useMemo } from 'react';

import { Link } from 'components/Link/Link';

import type { Availability } from 'types';
import type { MenuVariant, Product, Venue } from 'generated/graphql';

import { SelectMenu } from 'components/SelectMenu';

import { useAvailabilityTypeByVariablesByApp } from 'utils';
import { getMaxMinPrice } from 'utils/ListItem';
import { availabilities, AvailabilityStatus } from 'utils/constants';
import { useSetAvailability } from 'utils/Availability';

import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import useFeatureFlagsQuery from 'common/Hooks/UseFeatureFlagsQuery';
import styles from './ListItem.module.scss';
import AvailabilityPill from '../AvailabilityPill/AvailabilityPill';
import { createAppDataList } from './listUtils';
import { AppTile } from './AppTile';
import { getAppPriceAndNameList } from './AppOptions';
import ItemImageAndTitle from './ItemImageAndTitle';

const SNOOZE_FEATURE_FLAG = 'snoozeEnabled';
interface ListProps {
  menuItem: Product | MenuVariant;
  searchValue?: string;
  isLoading?: boolean;
  venue: Venue | undefined;
  hasBrands?: boolean;
}
interface SelectMenuOption {
  content: JSX.Element;
  value: string;
  type: 'button';
  hidden: boolean;
}

export const ListItem: React.FC<ListProps> = ({ menuItem, searchValue, isLoading, venue, hasBrands = false }) => {
  const [featureFlags, loading, error] = useFeatureFlagsQuery([SNOOZE_FEATURE_FLAG]);
  const isSnoozeEnabled = useMemo(() => {
    if (loading || error || !featureFlags) {
      return false;
    }
    const snoozeFeatureFlag = featureFlags[SNOOZE_FEATURE_FLAG];
    return !!snoozeFeatureFlag?.isEnabled;
  }, [featureFlags, loading, error]);
  const { name, variablesByApp, __typename: menuItemType, mmId } = menuItem;
  const imageUri = menuItemType === 'Product' ? menuItem.variablesByApp.find((vba) => vba.imageUri)?.imageUri : null;

  const availabilityType = useAvailabilityTypeByVariablesByApp(variablesByApp);
  const availabilityName = availabilities.find((a) => a.slug === availabilityType)?.name || '';

  const [minPrice, maxPrice] = getMaxMinPrice(menuItem);

  const [appPriceList, appNameList] = getAppPriceAndNameList({
    menuItemType,
    apps: variablesByApp,
    hasBrands,
    venueName: venue?.name
  });

  const hasSurcharge = useMemo(() => {
    return (
      venue?.menus?.some((menu) => {
        if (!menu.surcharge) {
          return false;
        }

        if (menu.surcharge.amount === 0) {
          return false;
        }

        if (!menu.products && !menu.variants) {
          return false;
        }
        return (
          menu.products.some((product) => product.mmId === menuItem.mmId) ||
          menu.variants.some((variant) => variant.mmId === menuItem.mmId)
        );
      }) || false
    );
  }, [venue, menuItem.mmId]);

  const [handleAvailabilityChange, availabilityChangeLoading] = useSetAvailability(menuItem, venue, false);

  const appTiles = useMemo(() => createAppDataList(variablesByApp), [variablesByApp]);
  const appTilesJsx = useMemo(() => {
    return appTiles.map((appData) => {
      return <AppTile key={appData.key} appData={appData} isLoading={isLoading} availabilityType={availabilityType} />;
    });
  }, [appTiles, availabilityType, isLoading]);

  const titleWithBoldSearchText = (() => {
    const trimmedSearchValue = searchValue?.trim();

    if (!trimmedSearchValue) {
      return name;
    }

    const indexOfTerm = name.toLowerCase().indexOf(trimmedSearchValue.toLowerCase());

    const term = name.substring(indexOfTerm, indexOfTerm + trimmedSearchValue.length);
    const beforeTerm = name.substring(0, indexOfTerm);
    const afterTerm = name.substring(indexOfTerm + trimmedSearchValue.length, name.length);

    return (
      <>
        {beforeTerm}
        <strong>{term}</strong>
        {afterTerm}
      </>
    );
  })();

  const onAvailabilityChangeClick = (value: string): void => {
    logAmplitudeEvent({
      name: `set ${menuItemType === 'Product' ? `item` : `option`} availability`,
      category:
        menuItemType === 'Product' ? AmplitudeEventCategories.MM_ALL_ITEMS : AmplitudeEventCategories.MM_OPTIONS,
      properties: {
        [AmplitudeEventProperties.ACTION]: `set ${
          value === AvailabilityStatus.UNAVAILABLE_TODAY ? 'unavailable until end of day' : value
        }`,
        ...(menuItemType === 'Product' && {
          [AmplitudeEventProperties.ITEM_POS_ID]: menuItem.posId,
          [AmplitudeEventProperties.ITEM_NAME]: menuItem.name
        }),
        ...(menuItemType === 'MenuVariant' && {
          [AmplitudeEventProperties.OPTION_POS_ID]: menuItem.posId,
          [AmplitudeEventProperties.OPTION_NAME]: menuItem.name
        })
      }
    });

    handleAvailabilityChange(value);
  };

  return (
    <li
      key={menuItem.mmId}
      className={styles.ListItem}
      data-testid="list-item"
      style={{
        pointerEvents: isLoading ? 'none' : undefined
      }}
    >
      <div className={`${styles.Title} ${menuItemType === 'Product' ? styles.TitleWithoutImage : ''}`}>
        {menuItemType === 'Product' ? (
          <Link href={`items/${menuItem.mmId}`}>
            <ItemImageAndTitle
              showImage
              imageUri={imageUri}
              name={name}
              availabilityType={availabilityType}
              isLoading={isLoading}
              titleWithBoldSearchText={titleWithBoldSearchText}
              minPrice={minPrice}
              maxPrice={maxPrice}
              appPriceList={appPriceList}
              itemHasSurcharge={hasSurcharge}
              mmId={mmId}
            />
          </Link>
        ) : (
          <ItemImageAndTitle
            showImage={false}
            imageUri={imageUri}
            name={name}
            availabilityType={availabilityType}
            isLoading={isLoading}
            titleWithBoldSearchText={titleWithBoldSearchText}
            minPrice={minPrice}
            maxPrice={maxPrice}
            appPriceList={appPriceList}
            itemHasSurcharge={hasSurcharge}
            mmId={mmId}
          />
        )}
      </div>
      <div className={styles.PriceRange} data-testid="list-item-price-range">
        <div className={styles.Surcharge}>{hasSurcharge && <span data-testid="list-item-surcharge">%</span>}</div>

        <SelectMenu
          options={minPrice === maxPrice ? [] : appPriceList}
          defaultButtonContent={
            <span className={isLoading ? 'LoadingCoverText' : ''}>
              {minPrice === maxPrice ? minPrice : `${minPrice} - ${maxPrice}`}
            </span>
          }
          buttonStyle={styles.Price}
          questionMark={!isLoading}
          downIcon={null}
          openOnHover
          align="center"
          size="small"
        />
      </div>
      <div className={styles.Logos} data-testid="list-item-logos">
        <SelectMenu
          options={appNameList}
          defaultButtonContent={<ul>{appTilesJsx}</ul>}
          buttonStyle={`${styles.Price}`}
          openOnHover
          align="center"
          downIcon={null}
          size="small"
        />
      </div>
      <div className={styles.Availability} data-testid="list-item-availability">
        <SelectMenu
          options={
            isLoading
              ? []
              : availabilities.reduce((acc: SelectMenuOption[], availability: Availability) => {
                  // Skip Unavailable Today when snooze is disabled or item type is not 'Product'
                  if (
                    (!isSnoozeEnabled || menuItemType !== 'Product') &&
                    availability.slug === AvailabilityStatus.UNAVAILABLE_TODAY
                  ) {
                    return acc;
                  }

                  acc.push({
                    content: (
                      <>
                        <i className={`Dot ${availability.slug}`} />
                        <span>{availability.name}</span>
                      </>
                    ),
                    value: availability.slug,
                    type: 'button',
                    hidden:
                      availability.slug === AvailabilityStatus.MIXED && availabilityType !== AvailabilityStatus.MIXED
                  });

                  return acc;
                }, [])
          }
          defaultButtonContent={
            isLoading ? (
              <span className="LoadingCoverText">Available</span>
            ) : (
              <AvailabilityPill loading={availabilityChangeLoading} availability={availabilityName} />
            )
          }
          value={availabilityType}
          forceUseDefaultButtonContent
          downIcon={null}
          onChange={onAvailabilityChangeClick}
          buttonStyle={styles.Price}
          openOnHover
          align="right"
          size="small"
        />
      </div>
    </li>
  );
};
